import React, { useState, useEffect } from 'react';
import {
  Form, Input, Button, Upload, Row, Col, Switch, Space, Typography, message
} from 'antd';
import {
  UploadOutlined, SaveOutlined, CloseOutlined, InboxOutlined,
  FileImageOutlined, FileTextOutlined, SettingOutlined
} from '@ant-design/icons';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { createBlogPost, updateBlogPost } from '../../../api/blogPostApi';

const { Text } = Typography;
const { TextArea } = Input;

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image'],
    ['blockquote'],
    ['clean']
  ]
};

const BlogPostForm = ({ blogPost, onSubmit, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [authorImageFileList, setAuthorImageFileList] = useState([]);
  const [content, setContent] = useState('');

  useEffect(() => {
    if (blogPost) {
      if (blogPost.coverImage) {
        setFileList([{
          uid: '-1',
          name: 'Current Cover Image',
          status: 'done',
          url: blogPost.coverImage
        }]);
      } else {
        setFileList([]);
      }

      if (blogPost.authorImage) {
        setAuthorImageFileList([{
          uid: '-2',
          name: 'Current Author Image',
          status: 'done',
          url: blogPost.authorImage
        }]);
      } else {
        setAuthorImageFileList([]);
      }

      setContent(blogPost.content || '');

      form.setFieldsValue({
        title: blogPost.title || '',
        excerpt: blogPost.excerpt || '',
        author: blogPost.author || '',
        tags: Array.isArray(blogPost.tags) ? blogPost.tags.join(', ') : '',
        isPublished: blogPost.isPublished || false,
      });
    } else {
      form.resetFields();
      setFileList([]);
      setAuthorImageFileList([]);
      setContent('');
    }
  }, [blogPost, form]);

  const customRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handleAuthorImageUploadChange = ({ fileList: newFileList }) => {
    setAuthorImageFileList(newFileList);
  };

  const onFinish = async (values) => {
    if (!content || content === '<p><br></p>') {
      message.error('Please enter the blog content');
      return;
    }
    if (fileList.length === 0 && !blogPost?.coverImage) {
      message.error('Please upload a cover image');
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();

      submitData.append('title', values.title);
      submitData.append('excerpt', values.excerpt || '');
      submitData.append('content', content);
      submitData.append('author', values.author || '');
      submitData.append('tags', values.tags || '');
      submitData.append('isPublished', values.isPublished ? 'true' : 'false');

      if (fileList.length > 0 && fileList[0].originFileObj) {
        submitData.append('coverImage', fileList[0].originFileObj);
      } else if (blogPost?.coverImage) {
        submitData.append('coverImageUrl', blogPost.coverImage);
      }

      if (authorImageFileList.length > 0 && authorImageFileList[0].originFileObj) {
        submitData.append('authorImage', authorImageFileList[0].originFileObj);
      } else if (authorImageFileList.length > 0 && blogPost?.authorImage) {
        // Kept the existing author image unchanged
        submitData.append('authorImageUrl', blogPost.authorImage);
      } else if (blogPost && !authorImageFileList.length) {
        // Removed during editing — clear it
        submitData.append('authorImageUrl', '');
      }

      if (blogPost) {
        await updateBlogPost(blogPost._id, submitData);
        message.success('Blog post updated successfully');
      } else {
        await createBlogPost(submitData);
        message.success('Blog post created successfully');
      }

      form.resetFields();
      setFileList([]);
      setAuthorImageFileList([]);
      setContent('');
      onSubmit();
    } catch (error) {
      console.error('Error saving blog post:', error);
      const errorMessage = error?.message || error?.msg || 'Failed to save blog post';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formStyle = {
    fontFamily: 'Rubik, sans-serif',
    fontSize: '16px',
    fontWeight: 400,
    color: '#000000'
  };

  const sectionStyle = {
    background: '#fafafa',
    border: '1px solid #f0f0f0',
    borderRadius: 8,
    padding: 16,
    marginBottom: 20
  };

  const sectionHeaderStyle = { marginBottom: 16 };

  return (
    <div style={{ fontFamily: 'Rubik, sans-serif' }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          isPublished: false
        }}
        style={formStyle}
      >
        {/* Basic Details */}
        <div style={sectionStyle}>
          <Space align="center" style={sectionHeaderStyle}>
            <FileImageOutlined style={{ color: '#470172' }} />
            <Text strong>Basic Details</Text>
          </Space>

          <Form.Item
            label={<Text type="secondary" style={{ fontSize: 13 }}>Title</Text>}
            name="title"
            rules={[
              { required: true, message: 'Please enter the title' },
              { max: 200, message: 'Title cannot exceed 200 characters' }
            ]}
            style={{ marginBottom: 16 }}
          >
            <Input placeholder="Enter blog post title" style={formStyle} />
          </Form.Item>

          <Form.Item
            label={<Text type="secondary" style={{ fontSize: 13 }}>Excerpt</Text>}
            name="excerpt"
            tooltip="Short summary shown on the homepage and listing page"
            rules={[{ max: 300, message: 'Excerpt cannot exceed 300 characters' }]}
            style={{ marginBottom: 16 }}
          >
            <TextArea rows={2} placeholder="Brief summary of the post..." style={formStyle} />
          </Form.Item>

          <Form.Item
            label={<Text type="secondary" style={{ fontSize: 13 }}>Cover Image</Text>}
            rules={[{ required: !blogPost, message: 'Please upload a cover image' }]}
            style={{ marginBottom: 0 }}
          >
            <Upload
              listType="picture-card"
              fileList={fileList}
              onChange={handleUploadChange}
              customRequest={customRequest}
              beforeUpload={(file) => {
                const isImage = file.type.startsWith('image/');
                if (!isImage) {
                  message.error('You can only upload image files!');
                }
                const isLt5M = file.size / 1024 / 1024 < 5;
                if (!isLt5M) {
                  message.error('Image must be smaller than 5MB!');
                }
                return isImage && isLt5M;
              }}
              maxCount={1}
            >
              {fileList.length === 0 && (
                <div>
                  <InboxOutlined style={{ fontSize: 40, color: '#470172' }} />
                  <div style={{ marginTop: 8, ...formStyle }}>Upload Image</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </div>

        {/* Content */}
        <div style={sectionStyle}>
          <Space align="center" style={sectionHeaderStyle}>
            <FileTextOutlined style={{ color: '#470172' }} />
            <Text strong>Content</Text>
          </Space>

          <div style={{ background: '#fff' }}>
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              modules={quillModules}
              placeholder="Write the blog post content here..."
              style={{ fontFamily: 'Rubik, sans-serif' }}
            />
          </div>
        </div>

        {/* Meta */}
        <div style={sectionStyle}>
          <Space align="center" style={sectionHeaderStyle}>
            <SettingOutlined style={{ color: '#470172' }} />
            <Text strong>Meta & Publishing</Text>
          </Space>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text type="secondary" style={{ fontSize: 13 }}>Author</Text>}
                name="author"
                style={{ marginBottom: 16 }}
              >
                <Input placeholder="e.g. AID Team" style={formStyle} />
              </Form.Item>
              <Form.Item
                label={<Text type="secondary" style={{ fontSize: 13 }}>Author Image</Text>}
                tooltip="Optional — small photo shown next to the author's name"
                style={{ marginBottom: 0 }}
              >
                <Upload
                  listType="picture-card"
                  fileList={authorImageFileList}
                  onChange={handleAuthorImageUploadChange}
                  customRequest={customRequest}
                  beforeUpload={(file) => {
                    const isImage = file.type.startsWith('image/');
                    if (!isImage) {
                      message.error('You can only upload image files!');
                    }
                    const isLt2M = file.size / 1024 / 1024 < 2;
                    if (!isLt2M) {
                      message.error('Author image must be smaller than 2MB!');
                    }
                    return isImage && isLt2M;
                  }}
                  maxCount={1}
                >
                  {authorImageFileList.length === 0 && (
                    <div>
                      <InboxOutlined style={{ fontSize: 24, color: '#470172' }} />
                      <div style={{ marginTop: 6, ...formStyle, fontSize: 13 }}>Upload</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text type="secondary" style={{ fontSize: 13 }}>Tags</Text>}
                name="tags"
                tooltip="Comma-separated, e.g. industry, policy, vidarbha"
                style={{ marginBottom: 16 }}
              >
                <Input placeholder="e.g. industry, policy, vidarbha" style={formStyle} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label={<Text type="secondary" style={{ fontSize: 13 }}>Publish now</Text>}
            name="isPublished"
            valuePropName="checked"
            tooltip="When off, the post is saved as a draft and stays hidden from the public site"
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>
        </div>

        {/* Action Buttons */}
        <Row justify="end" gutter={8}>
          <Col>
            <Button onClick={onCancel} icon={<CloseOutlined />} style={formStyle}>
              Cancel
            </Button>
          </Col>
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
              style={{
                ...formStyle,
                backgroundColor: '#470172',
                borderColor: '#470172',
                color: '#ffffff'
              }}
            >
              {blogPost ? 'Update' : 'Create'}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default BlogPostForm;
